import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { localStorageService } from '@/services/localStorage.service';
import { useAuth } from '@/app/providers/AuthProvider';
import { getDefaultRouteForUser } from '@/lib/getDefaultRoute';

const RemoteLoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const { initializeAuth, user } = useAuth();

  useEffect(() => {
    if (user) {
       const defaultRoute = getDefaultRouteForUser(user);
      navigate(defaultRoute, { replace: true });
    }
  }, [user]);

  useEffect(() => {
    const validateAndLogin = async () => {
      const token = searchParams.get('token');
      const logId = searchParams.get('logId');

      if (!token || !logId) {
        console.error('❌ Missing token or logId:', { token: !!token, logId: !!logId });
        setStatus('error');
        setErrorMessage('Invalid remote access link');
        return;
      }

      console.log('🔐 Starting remote access validation...', { logId });

      try {
        // Validate remote access token
        console.log('📤 Sending validation request...');
        const response = await axiosInstance.post('/auth/validate-remote-access', {
          token,
          logId,
        });

        console.log('✅ Validation response:', response.data);

        if (response.data.success && response.data.data) {
          const { user } = response.data.data;

          // Store auth token and remote access info
          console.log('💾 Storing remote access session...');
          localStorageService.storeToken(token);
          localStorageService.storeUser(user);
          localStorage.setItem('remoteAccessLogId', logId);
          localStorage.setItem('isRemoteAccess', 'true');

          console.log('✅ Remote access session stored successfully');
          setStatus('success');
          toast.success(`Logged in as ${user.firstName} ${user.lastName} (Remote Access)`);

          // Redirect to dashboard after brief delay
          setTimeout(() => {
            initializeAuth(user.firstChatbotId)
          }, 1500);
        }
      } catch (error: any) {
        console.error('❌ Remote access validation failed:', error);
        console.error('Error response:', error.response?.data);
        setStatus('error');
        const message =
          error.response?.data?.message ||
          'Failed to validate remote access session';
        setErrorMessage(message);
        toast.error(message);
      }
    };

    validateAndLogin();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Validating Access...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your remote access session
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Access Granted!
            </h2>
            <p className="text-gray-600">Redirecting to dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Access Denied
            </h2>
            <p className="text-red-600 mb-4">{errorMessage}</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoteLoginPage;
